import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, 
  Clock, Users, RefreshCw, BookOpen, Building2, 
  Monitor, FileText, CheckCircle, GraduationCap, 
  MapPin, User, ExternalLink, List, Grid3X3,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import { resolveUploadUrl } from '../../utils/media';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';

// Event type configuration
const EVENT_TYPES = {
  cours: { label: "Cours", color: "bg-violet-500", textColor: "text-violet-600", icon: BookOpen },
  entreprise: { label: "Entreprise", color: "bg-amber-500", textColor: "text-amber-600", icon: Building2 },
  cours_en_ligne: { label: "Cours en ligne", color: "bg-blue-500", textColor: "text-blue-600", icon: Monitor },
  cours_physique: { label: "Cours physique", color: "bg-emerald-500", textColor: "text-emerald-600", icon: GraduationCap },
  examen: { label: "Examen", color: "bg-red-500", textColor: "text-red-600", icon: FileText },
  revision: { label: "Révision", color: "bg-orange-500", textColor: "text-orange-600", icon: RefreshCw },
  devoir: { label: "Devoir", color: "bg-pink-500", textColor: "text-pink-600", icon: CheckCircle }
};

/** Local YYYY-MM-DD — never use toISOString() for calendar days (UTC shift). */
const toLocalDateKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const StudentPlanningPage = ({ onMenuClick }) => {
  // Data states
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // View states
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Modal states
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const getAvatarUrl = (avatarUrl) => resolveUploadUrl(avatarUrl);

  // Fetch student events
  const fetchEvents = useCallback(async () => {
    try {
      const response = await api.get('/planning/student/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Erreur chargement événements:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Calendar navigation
  const goToPrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const [downloading, setDownloading] = useState(false);

  const downloadPDF = async (view) => {
    setDownloading(true);
    try {
      const params = { view };
      if (view === 'month') {
        params.month = currentDate.getMonth() + 1;
        params.year = currentDate.getFullYear();
      } else {
        params.year = currentDate.getFullYear();
      }
      const response = await api.get('/planning/student/export/pdf', {
        params,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = view === 'month'
        ? `planning_${params.year}_${String(params.month).padStart(2, '0')}.pdf`
        : `planning_${params.year}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Planning téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloading(false);
    }
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    
    startDate.setDate(startDate.getDate() - ((startDate.getDay() + 6) % 7));
    
    const days = [];
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (7 - endDate.getDay()) % 7);
    
    const current = new Date(startDate);
    while (current <= endDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  // Get events for a specific day
  const getEventsForDay = (day) => {
    const dayStr = toLocalDateKey(day);
    return events.filter(e => e.event_date === dayStr);
  };

  // Get events for the list view: upcoming first (ascending), then past (most recent first)
  const getListEvents = () => {
    const ref = new Date();
    ref.setHours(0, 0, 0, 0);
    const upcoming = events
      .filter(e => new Date(e.event_date) >= ref)
      .sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
    const past = events
      .filter(e => new Date(e.event_date) < ref)
      .sort((a, b) => new Date(b.event_date) - new Date(a.event_date));
    return [...upcoming, ...past];
  };

  // Open event detail modal
  const openEventModal = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  // Format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  // Format time
  const formatTime = (time) => time?.substring(0, 5) || '';

  // Check if location is a URL
  const isLocationUrl = (location) => {
    if (!location) return false;
    return location.toLowerCase().includes('http') || 
           location.toLowerCase().includes('zoom') ||
           location.toLowerCase().includes('meet.google') ||
           location.toLowerCase().includes('teams');
  };

  // Get month/year title
  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  const calendarDays = generateCalendarDays();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Count upcoming events
  const upcomingEvents = events.filter(e => new Date(e.event_date) >= today);
  const thisWeekEvents = upcomingEvents.filter(e => {
    const eventDate = new Date(e.event_date);
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);
    return eventDate < nextWeek;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <Header onMenuClick={onMenuClick} />
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
              <CalendarIcon className="w-8 h-8 text-violet-600" />
              Mon Planning
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">
              {upcomingEvents.length} événement(s) à venir • {thisWeekEvents.length} cette semaine
            </p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button 
              variant={viewMode === 'calendar' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setViewMode('calendar')}
              className={viewMode === 'calendar' ? 'bg-violet-600 hover:bg-violet-700' : ''}
            >
              <Grid3X3 className="w-4 h-4 mr-2" />
              Calendrier
            </Button>
            <Button 
              variant={viewMode === 'list' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'bg-violet-600 hover:bg-violet-700' : ''}
            >
              <List className="w-4 h-4 mr-2" />
              Liste
            </Button>

            <div className="h-6 w-px bg-slate-300 dark:bg-slate-600 self-center mx-1" />

            <Button
              variant="outline"
              size="sm"
              disabled={downloading}
              onClick={() => downloadPDF('month')}
            >
              <Download className="w-4 h-4 mr-2" />
              PDF Mois
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={downloading}
              onClick={() => downloadPDF('year')}
            >
              <Download className="w-4 h-4 mr-2" />
              PDF Année
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-violet-50 dark:bg-violet-900/20 border-violet-200 dark:border-violet-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-500 rounded-lg">
                  <CalendarIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-violet-700 dark:text-violet-300">{upcomingEvents.length}</p>
                  <p className="text-xs text-violet-600 dark:text-violet-400">À venir</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                    {events.filter(e => e.event_type === 'cours' || e.event_type === 'cours_en_ligne' || e.event_type === 'cours_physique').length}
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400">Cours</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500 rounded-lg">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-700 dark:text-red-300">
                    {events.filter(e => e.event_type === 'examen').length}
                  </p>
                  <p className="text-xs text-red-600 dark:text-red-400">Examens</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500 rounded-lg">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                    {events.filter(e => e.event_type === 'entreprise').length}
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-400">Entreprise</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {viewMode === 'calendar' ? (
          <>
            {/* Calendar Navigation */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={goToPrevMonth}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={goToToday}>
                      Aujourd'hui
                    </Button>
                    <Button variant="outline" size="sm" onClick={goToNextMonth}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                    {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h2>
                  
                  <div className="w-32"></div>
                </div>
              </CardContent>
            </Card>

            {/* Calendar Grid */}
            <Card>
              <CardContent className="p-4">
                {/* Day headers */}
                <div className="grid grid-cols-7 mb-2">
                  {dayNames.map(day => (
                    <div key={day} className="text-center text-sm font-medium text-slate-500 dark:text-slate-400 py-2">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((day, idx) => {
                    const dayEvents = getEventsForDay(day);
                    const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                    const isToday = day.getTime() === today.getTime();
                    
                    return (
                      <div 
                        key={idx}
                        className={`
                          min-h-[100px] p-1 border rounded-lg
                          ${isCurrentMonth ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-900'}
                          ${isToday ? 'border-violet-500 border-2' : 'border-slate-200 dark:border-slate-700'}
                        `}
                      >
                        <div className={`
                          text-sm font-medium mb-1 text-center
                          ${isToday ? 'text-violet-600 dark:text-violet-400 font-bold' : 
                            isCurrentMonth ? 'text-slate-900 dark:text-white' : 'text-slate-400 dark:text-slate-600'}
                        `}>
                          {day.getDate()}
                        </div>
                        
                        <div className="space-y-1">
                          {dayEvents.slice(0, 3).map(event => {
                            const typeConfig = EVENT_TYPES[event.event_type] || EVENT_TYPES.cours;
                            return (
                              <button
                                key={event.id}
                                onClick={() => openEventModal(event)}
                                className={`
                                  w-full text-left text-xs p-1 rounded truncate
                                  ${typeConfig.color} text-white
                                  hover:opacity-80 transition-opacity
                                `}
                                title={event.title}
                              >
                                {formatTime(event.start_time)} {event.title}
                              </button>
                            );
                          })}
                          {dayEvents.length > 3 && (
                            <span className="text-xs text-slate-500 dark:text-slate-400 pl-1">
                              +{dayEvents.length - 3} autre(s)
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          /* List View */
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Tous les événements</CardTitle>
            </CardHeader>
            <CardContent>
              {getListEvents().length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun événement</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {getListEvents().map(event => {
                    const typeConfig = EVENT_TYPES[event.event_type] || EVENT_TYPES.cours;
                    const IconComponent = typeConfig.icon;
                    const eventDate = new Date(event.event_date);
                    const isThisWeek = eventDate < new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
                    
                    return (
                      <button
                        key={event.id}
                        onClick={() => openEventModal(event)}
                        className={`
                          w-full text-left p-4 rounded-lg border transition-all
                          hover:shadow-md hover:border-violet-300 dark:hover:border-violet-700
                          ${isThisWeek ? 'bg-violet-50 dark:bg-violet-900/20 border-violet-200 dark:border-violet-800' : 
                                        'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'}
                        `}
                      >
                        <div className="flex items-start gap-4">
                          <div className={`p-3 rounded-lg ${typeConfig.color}`}>
                            <IconComponent className="w-5 h-5 text-white" />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-slate-900 dark:text-white truncate">
                                {event.title}
                              </h3>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${typeConfig.color} text-white`}>
                                {typeConfig.label}
                              </span>
                            </div>
                            
                            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="w-4 h-4" />
                                {formatDate(event.event_date)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {formatTime(event.start_time)} - {formatTime(event.end_time)}
                              </span>
                              {event.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {isLocationUrl(event.location) ? 'En ligne' : event.location}
                                </span>
                              )}
                              {event.trainer_name && (
                                <span className="flex items-center gap-2">
                                  {event.trainer_avatar_url ? (
                                    <img 
                                      src={getAvatarUrl(event.trainer_avatar_url)} 
                                      alt={event.trainer_name}
                                      className="w-5 h-5 rounded-full object-cover"
                                    />
                                  ) : (
                                    <User className="w-4 h-4" />
                                  )}
                                  {event.trainer_name}
                                </span>
                              )}
                            </div>
                            
                            {/* Trainer notes preview */}
                            {(event.trainer_comment || event.trainer_instructions) && (
                              <div className="mt-2 p-2 bg-violet-100 dark:bg-violet-900/30 rounded text-xs text-violet-700 dark:text-violet-300">
                                <span className="font-medium">Note du formateur :</span>{' '}
                                {event.trainer_comment || event.trainer_instructions}
                              </div>
                            )}
                          </div>
                          
                          <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Legend */}
        <Card className="mt-6">
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Légende</h3>
            <div className="flex flex-wrap gap-4">
              {Object.entries(EVENT_TYPES).map(([key, config]) => (
                <div key={key} className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded ${config.color}`}></div>
                  <span className="text-sm text-slate-600 dark:text-slate-400">{config.label}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Event Detail Modal */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedEvent && (
                <>
                  {(() => {
                    const typeConfig = EVENT_TYPES[selectedEvent.event_type] || EVENT_TYPES.cours;
                    const IconComponent = typeConfig.icon;
                    return <IconComponent className={`w-5 h-5 ${typeConfig.textColor}`} />;
                  })()}
                  {selectedEvent.title}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedEvent && EVENT_TYPES[selectedEvent.event_type]?.label}
            </DialogDescription>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4 py-4">
              {/* Date & Time */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <CalendarIcon className="w-4 h-4" />
                  {formatDate(selectedEvent.event_date)}
                </div>
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <Clock className="w-4 h-4" />
                  {formatTime(selectedEvent.start_time)} - {formatTime(selectedEvent.end_time)}
                </div>
              </div>
              
              {/* Group */}
              {selectedEvent.group_name && (
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <Users className="w-4 h-4" />
                  Groupe : {selectedEvent.group_name}
                </div>
              )}
              
              {/* Trainer */}
              {selectedEvent.trainer_name && (
                <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center">
                    {selectedEvent.trainer_avatar_url ? (
                      <img 
                        src={getAvatarUrl(selectedEvent.trainer_avatar_url)} 
                        alt={selectedEvent.trainer_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <GraduationCap className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Formateur</p>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {selectedEvent.trainer_name}
                    </p>
                    {selectedEvent.trainer_specialization && (
                      <p className="text-sm text-emerald-600 dark:text-emerald-400">
                        {selectedEvent.trainer_specialization}
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Course */}
              {selectedEvent.course_name && (
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <BookOpen className="w-4 h-4" />
                  Cours : {selectedEvent.course_name}
                </div>
              )}
              
              {/* Location */}
              {selectedEvent.location && (
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <MapPin className="w-4 h-4" />
                  {isLocationUrl(selectedEvent.location) ? (
                    <a 
                      href={selectedEvent.location} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-violet-600 hover:underline flex items-center gap-1"
                    >
                      Rejoindre la visioconférence
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <span>Lieu : {selectedEvent.location}</span>
                  )}
                </div>
              )}
              
              {/* Description */}
              {selectedEvent.description && (
                <div className="pt-3 border-t">
                  <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Description</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {selectedEvent.description}
                  </p>
                </div>
              )}
              
              {/* Trainer enrichment */}
              {(selectedEvent.trainer_comment || selectedEvent.trainer_instructions || selectedEvent.trainer_resources) && (
                <div className="pt-3 border-t bg-violet-50 dark:bg-violet-900/20 p-4 rounded-lg">
                  <h4 className="font-medium text-violet-700 dark:text-violet-300 mb-3 flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" />
                    Notes du formateur
                  </h4>
                  
                  {selectedEvent.trainer_comment && (
                    <div className="mb-2">
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {selectedEvent.trainer_comment}
                      </p>
                    </div>
                  )}
                  
                  {selectedEvent.trainer_instructions && (
                    <div className="mb-2">
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Instructions :</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {selectedEvent.trainer_instructions}
                      </p>
                    </div>
                  )}
                  
                  {selectedEvent.trainer_resources && (
                    <div>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Ressources :</p>
                      {selectedEvent.trainer_resources.startsWith('http') ? (
                        <a 
                          href={selectedEvent.trainer_resources} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-violet-600 hover:underline flex items-center gap-1"
                        >
                          Accéder aux ressources
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          {selectedEvent.trainer_resources}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentPlanningPage;
